import React, { useState } from 'react';

interface ComplianceTask {
  id: string;
  title: string;
  description?: string;
  category: string;
  dueDate: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
  assignedToStaff?: { id: string; name: string };
  completedByStaff?: { id: string; name: string };
}

interface ComplianceChecklistProps {
  tasks: ComplianceTask[];
  onTaskComplete?: (taskId: string, notes?: string) => void;
  onTaskUpdate?: (taskId: string, updates: any) => void;
  isReadOnly?: boolean;
}

export function ComplianceChecklist({
  tasks,
  onTaskComplete,
  onTaskUpdate,
  isReadOnly = false,
}: ComplianceChecklistProps) {
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'in_progress':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'in_progress':
        return '🔄';
      default:
        return '⏳';
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && new Date().toDateString() !== new Date(dueDate).toDateString();
  };

  const daysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diff = due.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (tasks.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <p className="text-green-800">✅ All compliance tasks are up to date!</p>
      </div>
    );
  }

  const pendingTasks = tasks.filter((t) => t.status !== 'completed');
  const overdueTasks = pendingTasks.filter((t) => isOverdue(t.dueDate));
  const urgentTasks = pendingTasks.filter((t) => !isOverdue(t.dueDate) && daysUntilDue(t.dueDate) <= 7);

  return (
    <div className="space-y-4">
      {/* Alert Banner */}
      {overdueTasks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">
            ⚠️ {overdueTasks.length} overdue task{overdueTasks.length !== 1 ? 's' : ''} requiring immediate attention
          </p>
        </div>
      )}

      {urgentTasks.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 font-semibold">
            ⏰ {urgentTasks.length} task{urgentTasks.length !== 1 ? 's' : ''} due within the week
          </p>
        </div>
      )}

      {/* Task List */}
      <div className="space-y-3">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`border-2 rounded-lg p-4 transition-colors ${getStatusColor(task.status)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{getStatusIcon(task.status)}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{task.title}</h3>
                    <p className="text-sm text-gray-600">{task.category}</p>
                  </div>
                </div>

                {task.description && <p className="text-sm text-gray-700 mb-3 ml-11">{task.description}</p>}

                <div className="flex flex-wrap gap-3 items-center ml-11">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                    {task.priority.toUpperCase()}
                  </span>

                  <span className={`text-sm font-medium ${isOverdue(task.dueDate) ? 'text-red-600' : 'text-gray-600'}`}>
                    {isOverdue(task.dueDate)
                      ? `🔴 Overdue: ${new Date(task.dueDate).toLocaleDateString()}`
                      : `Due: ${new Date(task.dueDate).toLocaleDateString()} (${daysUntilDue(task.dueDate)} days)`}
                  </span>

                  {task.assignedToStaff && (
                    <span className="text-sm text-gray-600">👤 {task.assignedToStaff.name}</span>
                  )}
                </div>
              </div>

              {!isReadOnly && task.status !== 'completed' && (
                <button
                  onClick={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                  className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                >
                  {expandedTaskId === task.id ? 'Close' : 'Complete'}
                </button>
              )}
            </div>

            {/* Expanded Task Form */}
            {expandedTaskId === task.id && !isReadOnly && (
              <div className="mt-4 pt-4 border-t border-gray-300 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Completion Notes</label>
                  <textarea
                    value={notes[task.id] || ''}
                    onChange={(e) => setNotes({ ...notes, [task.id]: e.target.value })}
                    placeholder="Enter any completion notes or evidence..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (onTaskComplete) {
                        onTaskComplete(task.id, notes[task.id] || '');
                        setNotes({ ...notes, [task.id]: '' });
                        setExpandedTaskId(null);
                      }
                    }}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={() => setExpandedTaskId(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {task.status === 'completed' && task.completedByStaff && (
              <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                ✓ Completed by {task.completedByStaff.name}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
